import { backend_url } from '../constants';
import { getAccessToken } from '../graphql/client/utils';
import axiosInstance from './instance';

export enum ImageTypeEnum {
  PROFILE = 'profile',
  ASSET = 'asset',
  CLUB = 'club',
  GROUP = 'group',
}

const THREE_MINUTES_IN_MS = 180000;
const CHAT_IMAGE_REFRESH_WINDOW_MS = 5 * 60 * 1000;
const MAX_UPLOAD_ATTEMPTS = 3;

const waitBeforeRetry = (attempt: number) =>
  new Promise<void>((resolve) =>
    setTimeout(resolve, Math.pow(2, attempt - 1) * 1000),
  );

const getIsNetworkError = (error: unknown) =>
  error instanceof Error &&
  (error.message.includes('Network request failed') ||
    error.message.includes('Failed to fetch') ||
    error.message.includes('NetworkError') ||
    error.message.includes('fetch') ||
    error.name === 'AbortError');

const getUploadErrorMessage = async (response: Response) => {
  const errorText = await response.text();
  return `${response.status} - ${errorText}`;
};

const getIsLocalNetworkHostname = (hostname: string) =>
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname === '0.0.0.0' ||
  hostname.startsWith('192.168.') ||
  hostname.startsWith('10.') ||
  /^172\.(1[6-9]|2\d|3[01])\./.test(hostname);

const getBackendOrigin = () => {
  try {
    return new URL(backend_url).origin;
  } catch {
    return null;
  }
};

const getIsMessageScopedChatImageUrl = (parsedUrl: URL) => {
  const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);

  return (
    pathSegments[0] === 'chat-media' &&
    pathSegments[1] === 'messages' &&
    Boolean(pathSegments[2]) &&
    Boolean(pathSegments[3])
  );
};

export const normalizeChatImageUrlOrigin = (imageUrl: string): string => {
  let parsedUrl: URL;

  try {
    parsedUrl = new URL(imageUrl);
  } catch {
    return imageUrl;
  }

  if (!getIsMessageScopedChatImageUrl(parsedUrl)) {
    return imageUrl;
  }

  const backendOrigin = getBackendOrigin();

  if (!backendOrigin || parsedUrl.origin === backendOrigin) {
    return imageUrl;
  }

  const backendUrl = new URL(backendOrigin);
  const shouldUseBackendOrigin =
    getIsLocalNetworkHostname(parsedUrl.hostname) ||
    getIsLocalNetworkHostname(backendUrl.hostname);

  if (!shouldUseBackendOrigin) {
    return imageUrl;
  }

  return `${backendOrigin}${parsedUrl.pathname}${parsedUrl.search}`;
};

const createImageFormData = (
  file: File,
  type?: ImageTypeEnum,
  isAssetCreate?: boolean,
) => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  if (type) {
    formData.append('type', type);
  }

  if (isAssetCreate) {
    formData.append('isAssetCreate', 'true');
  }

  return formData;
};

export const uploadImageToTempStorage = async (
  file: File,
  type?: ImageTypeEnum,
  isAssetCreate?: boolean,
): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  if (type) {
    formData.append('type', type);
  }

  if (isAssetCreate) {
    formData.append('isAssetCreate', 'true');
  }

  try {
    const response = await axiosInstance.post('/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.tempUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

export const uploadActivityPhoto = async (
  activityId: string,
  file: File,
): Promise<{ _id: string; imageUrl: string; thumbnailUrl?: string }> => {
  const accessToken = getAccessToken();
  const formData = new FormData();
  formData.append('file', file, file.name);

  const response = await fetch(
    `${backend_url}/club-activities/${activityId}/photos`,
    {
      method: 'POST',
      headers: {
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error(`Upload failed: ${await getUploadErrorMessage(response)}`);
  }

  return response.json();
};

export const uploadActivityExpensePhoto = async ({
  contextType,
  contextId,
  file,
}: {
  contextType: string;
  contextId: string;
  file: File;
}): Promise<{ photoUrl: string }> => {
  const accessToken = getAccessToken();
  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('contextType', contextType);
  formData.append('contextId', contextId);

  const response = await fetch(`${backend_url}/activity-expenses/photo`, {
    method: 'POST',
    headers: {
      ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${await getUploadErrorMessage(response)}`);
  }

  return response.json();
};

export const uploadChatImage = async (file: File): Promise<{ url: string }> => {
  const accessToken = getAccessToken();
  let lastError: Error | unknown;

  for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
    const formData = createImageFormData(file);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), THREE_MINUTES_IN_MS);

    try {
      const response = await fetch(
        `${backend_url}/chat-messages/upload-image`,
        {
          method: 'POST',
          headers: {
            ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
          },
          body: formData,
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(
          `Chat image upload failed: ${await getUploadErrorMessage(response)}`,
        );
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      lastError = error;

      const isNetworkError = getIsNetworkError(error);

      if (!isNetworkError || attempt === MAX_UPLOAD_ATTEMPTS) {
        throw error;
      }

      await waitBeforeRetry(attempt);
    }
  }

  throw lastError;
};

export const refreshChatImageUrl = async (
  imageUrl: string,
  { force = false }: { force?: boolean } = {},
): Promise<string> => {
  const normalizedImageUrl = normalizeChatImageUrlOrigin(imageUrl);
  let parsedUrl: URL;

  try {
    parsedUrl = new URL(normalizedImageUrl);
  } catch {
    return imageUrl;
  }

  if (!getIsMessageScopedChatImageUrl(parsedUrl)) {
    return imageUrl;
  }

  const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
  const expiresAt = Number(parsedUrl.searchParams.get('expires'));
  const shouldRefresh =
    force ||
    !Number.isFinite(expiresAt) ||
    expiresAt - Date.now() <= CHAT_IMAGE_REFRESH_WINDOW_MS;

  if (!shouldRefresh) {
    return normalizedImageUrl;
  }

  const accessToken = getAccessToken();
  const messageId = pathSegments[2];
  const fileName = pathSegments.slice(3).join('/');
  const response = await fetch(
    `${backend_url}/chat-media/messages/${messageId}/${fileName}/signed-url`,
    {
      method: 'GET',
      headers: {
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Chat image URL refresh failed: ${await getUploadErrorMessage(response)}`,
    );
  }

  const data = await response.json();
  return data.url ? normalizeChatImageUrlOrigin(data.url) : normalizedImageUrl;
};
